import { AIChatSessionTypeEnum } from '@/types/generated'

export type ChatContext = 'case' | 'general'

/**
 * Map an AI chat session's type + case presence to the ``chat_context``
 * analytics dimension.
 *
 * Mirrors the backend classifier in ``AiChatMessageSentProperties.from_message``
 * (``app/metrics/properties.py``) so the FE-emitted ``ai_chat_model_selected``
 * event joins cleanly to the BE-emitted ``ai_chat_message_sent`` event on the
 * AI-432 dashboard.
 *
 * :param sessionType: The session's ``session_type`` enum value.
 * :param hasCase: Whether the session has an associated case. Relevant only
 *     for ``UNIFIED`` sessions — ``CASE``/``GLOBAL`` ignore this flag.
 * :return: ``'case'`` when the session is case-scoped (``CASE``, or
 *     ``UNIFIED`` with a case attached), otherwise ``'general'``.
 */
export const toChatContext = (sessionType: AIChatSessionTypeEnum, hasCase: boolean): ChatContext =>
  sessionType === AIChatSessionTypeEnum.CASE ||
  (sessionType === AIChatSessionTypeEnum.UNIFIED && hasCase)
    ? 'case'
    : 'general'

export interface ContextDeltaDoc {
  uuid: string
  filename: string
}

export interface ContextDeltaInput {
  /** All non-deleted documents currently attached to the session. */
  sessionDocuments: ContextDeltaDoc[]
  /** Documents uploaded since the last send that may not yet be in ``sessionDocuments``. */
  extraDocuments: ContextDeltaDoc[]
  /** Currently attached case label (if any). */
  caseLabel?: string
  /** Currently attached case uuid (if any). */
  caseUuid?: string
  /** Set of document uuids already announced in earlier banners for this session. */
  previouslyAnnotatedDocUuids: Set<string>
  /** Whether any earlier banner already announced the case. */
  caseAlreadyAnnotated: boolean
}

export interface ContextDelta {
  caseLabel?: string
  caseUuid?: string
  documents: ContextDeltaDoc[]
}

/**
 * Compute the **delta** payload for the next context banner.
 *
 * The chat thread renders a banner per attach turn rather than one cumulative
 * banner. To keep each banner scoped to its turn, callers must inject only
 * the docs/case that have not been announced before:
 *
 * - Document uuids already present in any prior banner are dropped.
 * - The case is dropped when an earlier banner already named it (the case
 *   label must appear in exactly one banner — the turn it was attached).
 *
 * :param input: The full context state (session docs + just-uploaded docs +
 *     case info) plus what's already been annotated.
 * :return: ``null`` when there is nothing new to announce; otherwise the
 *     delta payload to feed into ``useChatStream.injectContextMessage``.
 */
export function computeContextAnnotationDelta(input: ContextDeltaInput): ContextDelta | null {
  const knownDocs = new Map<string, ContextDeltaDoc>()
  for (const d of input.sessionDocuments) knownDocs.set(d.uuid, d)
  for (const d of input.extraDocuments) {
    if (!knownDocs.has(d.uuid)) knownDocs.set(d.uuid, d)
  }

  const newDocs = Array.from(knownDocs.values()).filter(
    (d) => !input.previouslyAnnotatedDocUuids.has(d.uuid)
  )

  const caseLabel = input.caseAlreadyAnnotated ? undefined : input.caseLabel
  const caseUuid = input.caseAlreadyAnnotated ? undefined : input.caseUuid

  if (!caseLabel && newDocs.length === 0) return null

  return {
    caseLabel: caseLabel || undefined,
    caseUuid: caseUuid || undefined,
    documents: newDocs
  }
}
